/**
 * What the API accepts for a task image.
 *
 * Mirrors `readImageUpload` in px's `src/admin/task/task.service.js`, so a file
 * that will be refused is refused here first — the API says the same thing,
 * but only after the whole file has crossed the wire.
 */

export const TASK_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

export const TASK_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
  "image/gif",
];

/** For an `<input type="file" accept>`. */
export const TASK_IMAGE_ACCEPT = TASK_IMAGE_TYPES.join(",");

/** Human-readable version of the same rule, for helper text. */
export const TASK_IMAGE_HINT = "PNG, JPG, WebP, SVG or GIF up to 2MB";

/**
 * Why this file cannot be used as a task image, or null if it can.
 * @param {File} file
 * @returns {string|null}
 */
export const taskImageFileError = (file) => {
  if (!file) return null;

  if (!TASK_IMAGE_TYPES.includes(String(file.type).toLowerCase())) {
    return `An image must be a PNG, JPEG, WebP, SVG or GIF — got ${file.type || "no type"}`;
  }
  if (file.size > TASK_IMAGE_MAX_BYTES) {
    return `That image is ${(file.size / (1024 * 1024)).toFixed(1)}MB — the limit is 2MB`;
  }
  return null;
};

/**
 * Give a pasted/dropped image a real filename.
 *
 * A screenshot or an image copied from another page arrives with a name like
 * "image.png", "blob", or nothing at all — which is fine on screen but is
 * what a multipart upload sends as the filename, so it's worth fixing before
 * it reaches the API.
 */
export const namedTaskImageFile = (file) => {
  if (!file) return null;

  const name = String(file.name || "");
  const looksNamed = name && name !== "blob" && /\.[a-z0-9]+$/i.test(name);
  if (looksNamed) return file;

  const extension = String(file.type || "image/png")
    .split("/")[1]
    .replace(/\+.*$/, "");

  return new File([file], `pasted-task-image-${Date.now()}.${extension}`, { type: file.type });
};

/**
 * Pull an image out of a paste, or null when the clipboard held none.
 * Anything that is not an image is left alone so ordinary text pasting
 * elsewhere in the modal still works.
 */
export const taskImageFromClipboard = (event) => {
  const items = event?.clipboardData?.items;
  if (!items) return null;

  for (const item of items) {
    if (item.kind === "file" && String(item.type).toLowerCase().startsWith("image/")) {
      const file = item.getAsFile();
      if (file) return namedTaskImageFile(file);
    }
  }
  return null;
};
